import { createApi } from '@reduxjs/toolkit/query/react'
import { API_ENDPOINTS } from 'Constants'
import {
  IHealthDetailsResponse,
  INestedQuestionSet,
  IPlanQuestionsResponse,
  IPropertyDetailsResponse,
  ISuitabilityQuestionsResponse,
} from 'Models/BOB/ResponseModels'
import {
  educationalDetailsRenderObject,
  illitrateDeclarationString,
  vernacularAndIllitrateFormObject,
  vernacularDeclarationString,
} from 'Pages/Life/BobLifeSteppers/AssuredAndPayorDetails/Forms/EducationalDetailsForm/educationalDetailsRenderObject'
import { personalDetailsRenderObject } from 'Pages/Life/BobLifeSteppers/AssuredAndPayorDetails/Forms/PersonalDetailsForm/personalDetailsRenderObject'
import personDisabilityRenderObject from 'Pages/Life/BobLifeSteppers/AssuredAndPayorDetails/Forms/PersonDisabilityDetailsForm/personDisabilityRenderObject'
import { bobMasterUrlQuery } from 'Services/baseQuery'
export interface ILifePincode {
  id?: number
  pincode?: number
  area_code?: number
  area_id?: number
  area_name?: string
  city_code?: string
  city_name?: string
  is_district?: boolean
  state_code?: string
  state_name?: string
  extra_info?: any
  is_insurance_wise?: boolean
  from_cache?: boolean
  district_code?: string
}
export interface IPincodeRes {
  count: number
  result: ILifePincode[]
}
export interface ILifePincodeOption extends ILifePincode {
  label: string
  value: string
}

export interface IBgrListBenefits {
  id: number
  name: string
  code: string
  description: string
  is_filter_enable: boolean
  is_compare_enable: boolean
  is_popular_filter: boolean
}

export const bobMasterApi = createApi({
  reducerPath: 'bobMasterApi',
  baseQuery: bobMasterUrlQuery,
  keepUnusedDataFor: 0.0001,
  refetchOnReconnect: true,
  refetchOnMountOrArgChange: true,
  refetchOnFocus: true,
  endpoints: (builder) => ({
    getChoices: builder.query<
      {
        [key: string]: {
          code: string
          name: string
        }[]
      },
      { [key: string]: string }
    >({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.GET_CHOICES_MASTER,
        // url: API_ENDPOINTS.BOB.GET_CHOICES,
        method: 'GET',
        params: params,
      }),
    }),
    getChoicesNoCache: builder.mutation<
      Record<string, { code: string; name: string }[]>,
      Record<string, string | string[]>
    >({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.GET_CHOICES_MASTER,
        // url: API_ENDPOINTS.BOB.GET_CHOICES,
        method: 'GET',
        params: params,
      }),
    }),
    getPincodeList: builder.query<
      IPincodeRes,
      {
        pincode: string
        insurance_id?: string
      }
    >({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.GET_PINCODE_LIST,
        method: 'GET',
        params: params,
      }),
    }),
    getSuitabilityQuestionsMaster: builder.query<
      ISuitabilityQuestionsResponse,
      {
        question_type: string
      }
    >({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.GET_MASTER_QUESTIONS_LIST,
        method: 'GET',
        params: params,
      }),
    }),
    getPlanQuestionsMaster: builder.query<
      IPlanQuestionsResponse,
      {
        plan: string
        age?: number
      }
    >({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.GET_MASTER_QUESTIONS_LIST,
        method: 'GET',
        params: params,
      }),
    }),

    getPlanQuestionsMasterNoCache: builder.mutation<
      IPlanQuestionsResponse,
      {
        plan: string
        age?: number
      }
    >({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.GET_MASTER_QUESTIONS_LIST,
        method: 'GET',
        params: params,
      }),
    }),

    getAcrQuestionsMaster: builder.query<
      any,
      {
        question_type: string
      }
    >({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.GET_MASTER_QUESTIONS_LIST,
        method: 'GET',
        params: params,
      }),
    }),
    getHealthDetailsForm: builder.query<
      IHealthDetailsResponse,
      {
        plan: string
        age: string | number
      }
    >({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.GET_MASTER_QUESTIONS_LIST,
        method: 'GET',
        params: params,
      }),
    }),
    getPropertyDetailsForm: builder.query<
      IPropertyDetailsResponse,
      {
        question_type: 'PropertyDetails'
      }
    >({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.GET_MASTER_QUESTIONS_LIST,
        method: 'GET',
        params: params,
      }),
    }),
    getPropertyDetailsFormFire: builder.query<
      any,
      {
        question_type: string
      }
    >({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.GET_MASTER_QUESTIONS_LIST,
        method: 'GET',
        params: params,
      }),
    }),
    getBgrQuationsList: builder.query<
      { [key: string]: INestedQuestionSet[] },
      {
        question_type: string
      }
    >({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.GET_MASTER_QUESTIONS_LIST,
        method: 'GET',
        params: params,
      }),
    }),
    getPersonalDetailsFormMaster: builder.query<{ form_fields: INestedQuestionSet[] }, null>({
      queryFn: () => {
        const formData: { form_fields: INestedQuestionSet[] } = personalDetailsRenderObject
        return {
          data: formData,
        }
      },
    }),
    getPersonDisabilityDetailsFormMaster: builder.query<
      { form_fields: INestedQuestionSet[] },
      null
    >({
      queryFn: () => {
        const formData: { form_fields: INestedQuestionSet[] } = personDisabilityRenderObject
        return {
          data: formData,
        }
      },
    }),
    getEducationalDetailsFormMaster: builder.query<
      {
        educationalDetailsRenderObject: { form_fields: INestedQuestionSet[] }
        vernacularDeclarationString: string
        illitrateDeclarationString: string
        vernacularAndIllitrateFormObject: { form_fields: INestedQuestionSet[] }
      },
      null
    >({
      queryFn: () => {
        const educationalDetailsformQuestions: { form_fields: INestedQuestionSet[] } =
          educationalDetailsRenderObject
        const vernacularAndIllitrateFormQuestions: { form_fields: INestedQuestionSet[] } =
          vernacularAndIllitrateFormObject
        return {
          data: {
            educationalDetailsRenderObject: educationalDetailsformQuestions,
            vernacularDeclarationString: vernacularDeclarationString,
            illitrateDeclarationString: illitrateDeclarationString,
            vernacularAndIllitrateFormObject: vernacularAndIllitrateFormQuestions,
          },
        }
      },
    }),
    getRiderMaster: builder.query<
      INestedQuestionSet[],
      {
        plan: string
        plan_data: Record<string, string | number | boolean | string[]>
        la_pr_equal: boolean
        age?: number
      }
    >({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.GET_RIDER,
        method: 'POST',
        body: body,
      }),
    }),
    getBranchDetailsList: builder.query<
      { bank_name: string; branch_name: string; extra_info: string; ifsc_code: string }[],
      {
        IFSC_Code: string
      }
    >({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.GET_BRANCH_DETAILS_LIST,
        method: 'GET',
        params: params,
      }),
    }),
    validatePan: builder.mutation<
      {
        pan_validated: boolean
        first_name?: string
        middle_name?: string
        last_name?: string
      },
      { pan_number: string }
    >({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.VALIDATE_PAN,
        method: 'POST',
        body: body,
      }),
    }),
    getBgrCostomerConsentLink: builder.query<{ link: string; consent_id: string }, any>({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.GET_BGR_CUSTOMER_CONSENT_LINK,
        method: 'POST',
        body: body,
      }),
    }),
    getBgrCostomerConsentStatus: builder.query<
      {
        status: 'APPROVED' | 'UNAPPROVED' | 'REJECTED'
        comment: string
        retryable: boolean
      },
      { consent_id?: string }
    >({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.GET_BGR_CUSTOMER_CONSENT_STATUS,
        method: 'GET',
        params: params,
      }),
    }),
    getPlanInformation: builder.query<
      any,
      {
        plan_code: string
      }
    >({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.GET_PLAN_DATA,
        method: 'GET',
        params: params,
      }),
    }),

    bgrListBenefits: builder.query<IBgrListBenefits[], any>({
      query: (param) => ({
        url: API_ENDPOINTS.BOB.BGR_LIST_BENEFITS,
        method: 'GET',
        params: param,
      }),
    }),

    getBgrPlanCompaire: builder.query<any, any>({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.BGR_COMPAIRE_PLANS,
        method: 'GET',
        params: params,
      }),
    }),
  }),
})

export const {
  useGetChoicesQuery,
  useLazyGetChoicesQuery,
  useGetChoicesNoCacheMutation,
  useLazyGetPincodeListQuery,
  useGetSuitabilityQuestionsMasterQuery,
  useGetHealthDetailsFormQuery,
  useLazyGetHealthDetailsFormQuery,
  useLazyGetPlanQuestionsMasterQuery,
  useGetAcrQuestionsMasterQuery,
  useGetPersonalDetailsFormMasterQuery,
  useGetEducationalDetailsFormMasterQuery,
  useGetPersonDisabilityDetailsFormMasterQuery,
  useLazyGetRiderMasterQuery,
  useLazyGetBranchDetailsListQuery,
  useValidatePanMutation,
  useGetPropertyDetailsFormQuery,
  useGetPropertyDetailsFormFireQuery,
  useGetBgrQuationsListQuery,
  useGetPlanQuestionsMasterNoCacheMutation,

  // bgr
  useLazyGetBgrCostomerConsentLinkQuery,
  useLazyGetBgrCostomerConsentStatusQuery,
  useLazyGetPlanInformationQuery,
  useLazyBgrListBenefitsQuery,
  useLazyGetBgrPlanCompaireQuery,
} = bobMasterApi
